/** @jsxImportSource ./simboluri-jsx */
// Verificări — suprafața GLOBALĂ a verdictelor structurii, pe TEMATICI
//
//
// Ce arată: TOATE verificările, din toți pașii care emit verdicte — inclusiv
// driftul, modurile proprii, regulile de amplasare și cele 118 verificări de
// fundație. Ierarhia are trei trepte, ca în catalog: TEMATICA (moduri, drift,
// fundație, grinzi, stâlpi, noduri, pereți, plăci, amplasare) → TIPUL de
// verificare → verificările individuale.
//
// FORMA e cea a ecranului de verificări al arhitecturii (aceleași cutii
// `ver-*`): două coloane de înălțime fixă, fiecare cu scrollul ei — în stânga
// „cum stau" și PE CE MĂ UIT, în dreapta „ce anume". Regulile ecranului:
//
//   * cele trei filtre stau împreună, într-un singur panou din stânga:
//     verdictul, treapta de severitate și TEMATICA. Tematica era deja acolo,
//     dar arăta ca o navigare, nu ca a treia fațetă a aceleiași întrebări;
//   * combinarea lor se vede. Fiecare fațetă își numără valorile CU CELELALTE
//     filtre puse (numărătoare pe fațete), deci alegând «pică» vezi pe loc
//     câte dintre picate sunt HIGH; iar deasupra listei stă propoziția
//     întreagă — „23 din 318 · Tematică X ȘI Verdict Y ȘI Severitate Z";
//   * căutarea a plecat. Era un al patrulea filtru, ascuns în capul paginii,
//     care se combina tăcut cu celelalte trei — exact confuzia pe care ecranul
//     o repara. (Nu avea nimic pe server: filtrarea era toată în browser.)
//   * nu există pastila «informative» în filtre;
//   * DIRECȚIA are coloana ei. Driftul se măsoară pe X și pe Y, dar tabloul
//     citea doar agregatul — v. `verificari.ts`, secțiunea Pasului 10.

import { useRegistruOptional } from "../nav/Registru";
import { numeRand } from "../nav/selectie";
import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import { t, useT, useTn } from "../i18n";
import type { Cheie } from "../i18n";
import { CatalogVerificari, categoriaDin } from "./tipuri";
import { getVerificariRanduri, getVerificariSumar } from "./apiStructura";
import { useCatalogVerificari } from "./useCatalog";
import VerificareDetaliu, { RezumatCategorie, costScurt } from "./VerificareDetaliu";
import TitluVerificare from "./Verificare";
import { BaraRE } from "./ElementPage";
import { TIP_ETICHETA, nr } from "./elemente";
import { hashElement, hashSectiune, hashVerificare, linkAncora, titluPas } from "./legaturi";
import {
  AcoperireVerificari,
  CelulaVerificari,
  GrupTematica,
  GrupTip,
  NR_TREPTE,
  ORDINEA_VERDICTELOR,
  PAGINA_CARD,
  PAGINA_INCA,
  RandVerificare,
  SEVERITATI,
  Severitate,
  SumarVerificari,
  Verdict,
  numaraSeveritati,
  numaraVerdicte,
  picateGlobale,
  severitateaDin,
  tematiciVerificari,
  tipuriVerificari,
} from "./verificari";
import { traduceRezultat } from "./texteRezultat";
import SeIncarca from "../components/SeIncarca";

// Vocabularul verdictelor, într-un singur loc: eticheta pastilei, tonul de
// culoare și textul chipului din rând.
//
// `informativ` NU e printre ele: e o valoare raportată, nu un verdict (tasarea
// relativă între axe, G11), iar o pastilă de filtrare pentru el promitea o
// alegere care pe majoritatea proiectelor nu are ce selecta („scoate filtrul
// de informative, că nu avem"). Rândurile
// informative rămân în tablou, sub tipul lor, cu chipul lor.
const VERDICTE: {
  key: Verdict;
  ton: string;
  pastila: Cheie;
  chip: Cheie;
}[] = [
  { key: "neconform", ton: "bad",
    pastila: "struct.vp.pastila_pica", chip: "struct.vp.chip_pica" },
  { key: "la_limita", ton: "amb",
    pastila: "struct.vp.pastila_limita", chip: "struct.vp.chip_limita" },
  { key: "conform", ton: "ok",
    pastila: "struct.vp.pastila_trec", chip: "struct.vp.chip_trece" },
  { key: "neaplicabil", ton: "mut",
    pastila: "struct.vp.pastila_nu_se_cer", chip: "struct.vp.chip_nu_se_cere" },
];

const TON: Record<Verdict, string> = {
  neconform: "bad",
  la_limita: "amb",
  conform: "ok",
  neaplicabil: "mut",
  informativ: "out",
};
const CHIP: Record<Verdict, Cheie> = {
  neconform: "struct.vp.pastila_pica",
  la_limita: "struct.vp.pastila_limita",
  conform: "struct.vp.chip_trece",
  neaplicabil: "struct.vp.chip_nu_se_cere",
  informativ: "struct.vp.chip_informativ",
};

/** Alegerea „toate tematicile": a treia fațetă trebuie să se poată DESFACE ca
 *  și celelalte două, altfel „ce pică în toată clădirea" nu se poate întreba —
 *  tematica rămânea singurul filtru pe care nu-l puteai scoate. */
const TOATE = "*";

/** Comutarea unei pastile: un set gol înseamnă „fără filtru", iar apăsarea
 *  ultimei pastile active îl golește. */
function comuta<T>(set: ReadonlySet<T>, v: T): Set<T> {
  const nou = new Set(set);
  if (nou.has(v)) nou.delete(v);
  else nou.add(v);
  return nou;
}

/** Cuvintele unei fațete cu mai multe valori alese: în interiorul unei fațete
 *  valorile se adună („pică SAU la limită"), între fațete se intersectează
 *  („ȘI"). Propoziția o spune, ca să nu rămână de ghicit. */
function sau(parti: string[]): string {
  if (parti.length <= 1) return parti[0] ?? "";
  return `${parti.slice(0, -1).join(", ")} sau ${parti[parti.length - 1]}`;
}

export default function VerificariPage({
  id,
  solutie,
  cloneId,
  versiune = "",
  corp,
  taburi,
}: {
  id: string;
  solutie?: string | null;
  /** spațiul din care se citește tabloul: al corpului activ, altfel al
   *  rândului (`StructureContext.cloneId`) */
  cloneId: string | null;
  /** versiunea pașilor spațiului (din rezumatele lor): când se schimbă,
   *  tabloul se cere din nou — serverul răspunde 304 dacă n-a atins nimic
   *  din ce adună */
  versiune?: string;
  /** corpul ale cărui verificări sunt pe ecran, la o soluție cu mai multe
   *  clădiri (null / absent la o singură clădire): verificările globale —
   *  moduri, drift, cutia rigidă — sunt ale fiecărui corp, iar «ansamblu»
   *  le poartă pe cele ale infrastructurii comune */
  corp?: string | null;
  // stripul de discipline al suprafeței de verificări (arhitectură/structură):
  // stă în pagină, nu deasupra ei, ca să se alinieze cu titlul — la fel ca pe
  // ecranul arhitecturii
  taburi?: ReactNode;
}) {
  // Numele soluției, nu id-ul ei: pastila scria «soluția 1783ba7b-903d-4…».
  const registrul = useRegistruOptional();
  const randSolutie = solutie
    ? registrul?.registru?.solutii.find((r) => r.id === solutie) ?? null : null;
  const numeSolutie = randSolutie ? numeRand(randSolutie) : null;
  const tr = useT();
  const trn = useTn();
  const catalog = useCatalogVerificari();

  // SUMARUL tabloului, compus pe server (`structure/tablou_verificari.py`):
  // celulele, nu rândurile. Ținut cu spațiul lui, ca schimbarea corpului să
  // arate locul cifrelor, iar o versiune nouă a aceluiași spațiu să înlocuiască
  // cifrele vechi abia când au sosit cele noi.
  const cheieSpatiu = `${id}|${cloneId ?? ""}`;
  const [sumar, setSumar] = useState<{ cheie: string; s: SumarVerificari } | null>(null);
  const [eroare, setEroare] = useState(false);
  useEffect(() => {
    if (!cloneId) {
      setSumar({ cheie: cheieSpatiu, s: { total: 0, celule: [], acoperire: [] } });
      return;
    }
    let viu = true;
    setEroare(false);
    getVerificariSumar(id, cloneId)
      .then((s) => { if (viu) setSumar({ cheie: cheieSpatiu, s }); })
      .catch(() => { if (viu) setEroare(true); });
    return () => { viu = false; };
  }, [id, cloneId, cheieSpatiu, versiune]);
  const sumarAici = sumar?.cheie === cheieSpatiu ? sumar.s : null;

  const randuri = useMemo(() => sumarAici?.celule ?? [], [sumarAici]);
  const contor = useMemo(() => numaraVerdicte(randuri), [randuri]);
  // picatele GLOBALE decid culoarea cadranului: ele invalidează restul
  // verdictelor, cele locale nu (v. `Cadran`)
  const globale = useMemo(() => picateGlobale(randuri, catalog), [randuri, catalog]);

  const [doar, setDoar] = useState<ReadonlySet<Verdict>>(new Set());
  // filtrul pe TREAPTA de severitate: „ce se repară întâi" e o întrebare la
  // fel de des pusă ca „ce pică"
  // Se intră cu fațeta de tematică DESFĂCUTĂ. Înainte ecranul alegea singur
  // prima tematică cu picate — un filtru pus pe tăcute, care e chiar
  // neînțelegerea de reparat: „1 din 4.965" arăta ca o clădire cu o singură
  // verificare, nu ca o felie dintr-un tablou.
  const [tema, setTema] = useState<string>(TOATE);
  const [sev, setSev] = useState<ReadonlySet<Severitate>>(new Set());
  const [tipDeschis, setTipDeschis] = useState<string | null>(null);
  const [randDeschis, setRandDeschis] = useState<string | null>(null);

  // NUMĂRĂTOAREA PE FAȚETE. Fiecare filtru își numără valorile pe rândurile
  // care trec prin CELELALTE două — altfel cifra de lângă „HIGH" e numărul din
  // toată clădirea, iar omul care tocmai a apăsat «pică» crede că atâtea picate
  // are. Așa, cifrele se mișcă la fiecare apăsare, și se vede că filtrele se
  // intersectează.
  const potriveste = useMemo(() => {
    const verdict = (x: CelulaVerificari) => !doar.size || doar.has(x.verdict);
    const treapta = (x: CelulaVerificari) => {
      if (!sev.size) return true;
      const s = severitateaDin(catalog, x.tip);
      return s ? sev.has(s) : false;
    };
    return { verdict, treapta };
  }, [doar, sev, catalog]);

  // fără filtrul de TEMATICĂ: pe rândurile astea se numără tematicile din
  // stânga
  const faraFiltrulDeTematica = useMemo(
    () => randuri.filter((x) => potriveste.verdict(x) && potriveste.treapta(x)),
    [randuri, potriveste]
  );
  const tematici = useMemo(
    () => tematiciVerificari(faraFiltrulDeTematica, catalog),
    [faraFiltrulDeTematica, catalog]
  );
  // UNIVERSUL tematicilor — toate cele care există pe proiect, filtrate sau
  // nu. Lista din stânga se desenează pe el, cu cifrele fațetei: o tematică
  // rămasă fără rânduri se STINGE, nu dispare. „Nicio picată HIGH la fundație"
  // e chiar răspunsul pe care omul îl caută când combină două filtre, iar un
  // rând care se evaporă îl ascunde — la fel ca la pastile.
  const toateTematicile = useMemo(
    () => tematiciVerificari(randuri, catalog),
    [randuri, catalog]
  );

  // `TOATE` = fațeta e desfăcută, dreapta arată toate tematicile una sub alta.
  // Tematica aleasă rămâne aleasă chiar dacă celelalte două filtre o golesc:
  // atunci se vede cu zero, nu se schimbă pe furiș cu alta.
  const toateTemele = tema === TOATE;
  const activaUnivers = toateTemele
    ? undefined
    : toateTematicile.find((t) => t.cheie === tema);
  const activa: GrupTematica | undefined =
    activaUnivers &&
    (tematici.find((t) => t.cheie === tema) ?? {
      ...activaUnivers,
      total: 0, neconform: 0, la_limita: 0, conform: 0,
      neaplicabil: 0, informativ: 0,
    });
  const cheieTema = activaUnivers?.cheie ?? null;

  const potrivesteTema = useMemo(() => {
    return (x: CelulaVerificari) =>
      cheieTema === null || (x.tematica ?? "") === cheieTema;
  }, [cheieTema]);

  const faraFiltrulDeVerdict = useMemo(
    () => randuri.filter((x) => potriveste.treapta(x) && potrivesteTema(x)),
    [randuri, potriveste, potrivesteTema]
  );
  const faraFiltrulDeSeveritate = useMemo(
    () => randuri.filter((x) => potriveste.verdict(x) && potrivesteTema(x)),
    [randuri, potriveste, potrivesteTema]
  );
  const filtrate = useMemo(
    () => faraFiltrulDeTematica.filter(potrivesteTema),
    [faraFiltrulDeTematica, potrivesteTema]
  );

  const contorVerdict = useMemo(
    () => numaraVerdicte(faraFiltrulDeVerdict),
    [faraFiltrulDeVerdict]
  );
  const contorSeveritate = useMemo(
    () => numaraSeveritati(faraFiltrulDeSeveritate, catalog),
    [faraFiltrulDeSeveritate, catalog]
  );
  const contorFaraTema = useMemo(
    () => numaraVerdicte(faraFiltrulDeTematica),
    [faraFiltrulDeTematica]
  );

  // grupele de tipuri desenate în dreapta: una singură (tematica activă) sau
  // câte una per tematică
  const grupe = useMemo(() => {
    const perTematica = (t: GrupTematica) =>
      tipuriVerificari(
        filtrate.filter((x) => (x.tematica ?? "") === t.cheie),
        catalog
      );
    if (toateTemele) return tematici.map((t) => ({ t, tipuri: perTematica(t) }));
    return activa ? [{ t: activa, tipuri: perTematica(activa) }] : [];
  }, [toateTemele, tematici, activa, filtrate, catalog]);
  const nrTipuri = grupe.reduce((n, g) => n + g.tipuri.length, 0);
  const nrFiltrate = filtrate.reduce((n, x) => n + x.n, 0);
  // filtrul de verdict, ca listă stabilă: îl primesc rândurile cerute de card
  const verdicte = useMemo(() => [...doar].sort(), [doar]);

  // Propoziția filtrului: fațetele, în ordinea în care se citesc, legate cu
  // «ȘI». Fiecare își poartă butonul de desfacere.
  const facete: { grup: string; text: string; sterge: () => void }[] = [];
  if (activa)
    facete.push({
      grup: tr("struct.vp.grup_tematica"),
      text: tr(activa.eticheta as Cheie),
      sterge: () => setTema(TOATE),
    });
  if (doar.size)
    facete.push({
      grup: tr("struct.vp.grup_verdict"),
      text: sau(VERDICTE.filter((v) => doar.has(v.key)).map((v) => tr(v.pastila))),
      sterge: () => setDoar(new Set()),
    });
  if (sev.size)
    facete.push({
      grup: tr("struct.vp.grup_severitate"),
      text: sau(
        SEVERITATI.filter((s) => sev.has(s.cod))
          .map((s) => tr(s.eticheta))
      ),
      sterge: () => setSev(new Set()),
    });

  const desfaTot = () => {
    setDoar(new Set());
    setSev(new Set());
    setTema(TOATE);
    setTipDeschis(null);
    setRandDeschis(null);
  };

  // TABLOUL SE ARATĂ ÎNTREG SAU DELOC: până la sumar și catalog (tematica și
  // severitatea vin din el) stă locul cifrelor, nu o cifră parțială și nici
  // un «gol» care nu e adevărat.
  if (!sumarAici || !catalog) {
    return (
      <div className="page verificari">
        {taburi}
        <div className="page-head">
          <div className="disp">{tr("struct.vp.titlu")}</div>
        </div>
        {eroare ? <p className="note bad">{tr("struct.vp.esec")}</p> : <SeIncarca />}
      </div>
    );
  }

  if (contor.total === 0) {
    return (
      <div className="page verificari">
        {taburi}
        <div className="page-head">
          <div className="disp">{tr("struct.vp.titlu")}</div>
        </div>
        <p className="note">
          {tr("struct.vp.gol")}{" "}
          <a href={hashSectiune(id, "pipeline", solutie ?? null)}>
            {tr("struct.act.link_pipeline")}
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="page verificari">
      {taburi}
      <div className="ver-doua">
        {/* --- Stânga: cum stau, apoi pe ce mă uit ------------------------ */}
        <div className="ver-stanga">
          <div className="page-head">
            <div className="disp">
              {tr("struct.vp.titlu")}
              {corp ? (
                <span
                  className="chip"
                  style={{ marginLeft: 8, fontSize: 12, verticalAlign: "middle" }}
                  title={
                    corp === "ansamblu"
                      ? tr("struct.vp.corp_ansamblu")
                      : tr("struct.vp.corp_titlu", { corp })
                  }
                >
                  {tr("struct.vp.corp", { corp })}
                </span>
              ) : null}
            </div>
            {/* Numele soluției poate fi un uuid — mai lat decât toată coloana
                din stânga. `.chip` e `white-space: nowrap`, deci pastila ieșea
                din panou peste primul rând al coloanei din dreapta, „N din M
                verificări". Acum se taie cu «…» (v. `.ver-solutie`), iar
                întregul stă în `title`. Cuvântul „Structură" a ieșit din
                pastilă: îl spune deja tabul disciplinei, de deasupra. */}
            <span
              className="chip c-acc ver-solutie"
              title={solutie
                ? tr("struct.vp.solutie_titlu", { solutie })
                : tr("struct.vp.disciplina")}
            >
              {solutie
                ? tr("struct.vp.solutia", { solutie: numeSolutie ?? solutie })
                : tr("struct.vp.disciplina")}
            </span>
          </div>

          <div className="card ver-sumar ver-sumar-solo">
            <Cadran contor={contor} globale={globale} />
          </div>

          <div className="card ver-filtre">
            <div className="ver-filtre-cap">
              <span className="lbl">{tr("struct.vp.filtre")}</span>
              <span className="note ver-filtre-ce">
                {tr("struct.vp.filtre_ce")}
              </span>
            </div>

            <Fateta
              titlu={tr("struct.vp.grup_verdict")}
              ce={tr("struct.vp.verdict_ce")}
              gol={doar.size === 0}
              onGol={() => setDoar(new Set())}
            >
              {VERDICTE.map((v) => {
                const n = contorVerdict[v.key];
                return (
                  <button
                    key={v.key}
                    type="button"
                    className={`chip c-${v.ton} ${doar.has(v.key) ? "on" : ""} ${
                      n ? "" : "ver-nul"
                    }`}
                    title={tr("struct.vp.arata_numai", { ce: tr(v.chip) })}
                    onClick={() => setDoar(comuta(doar, v.key))}
                  >
                    {tr(v.pastila)} <b>{nr(n)}</b>
                  </button>
                );
              })}
            </Fateta>

            {/* TREAPTA DE SEVERITATE — „cât de departe se propagă o picată din
                categoria asta", adică ordinea în care se repară. E altă
                întrebare decât „ce pică", deci altă fațetă. */}
            <Fateta
              titlu={tr("struct.vp.grup_severitate")}
              ce={tr("struct.vp.severitate_ce")}
              gol={sev.size === 0}
              onGol={() => setSev(new Set())}
            >
              {SEVERITATI.map((s) => {
                const n = contorSeveritate[s.cod];
                return (
                  <button
                    key={s.cod}
                    type="button"
                    className={`sev s-${s.cod} ${sev.has(s.cod) ? "on" : ""} ${
                      n ? "" : "ver-nul"
                    }`}
                    title={tr("struct.vp.sev_titlu", { cod: s.cod, ce: tr(s.ce) })}
                    onClick={() => setSev(comuta(sev, s.cod))}
                  >
                    <Treapta s={s} /> <b>{nr(n)}</b>
                  </button>
                );
              })}
            </Fateta>

            {/* A TREIA FAȚETĂ, în aceeași cutie cu primele două: tematica era
                aici și înainte, dar ca listă de navigare — nu se citea ca
                filtru, deci nici combinarea cu celelalte două. */}
            <div className="ver-fateta ver-fateta-tema">
              <div className="ver-fateta-cap">
                <span className="ob-lbl">{tr("struct.vp.grup_tematica")}</span>
                <span className="note">{tr("struct.vp.tematica_ce")}</span>
              </div>
              <div className="ver-cat">
                <button
                  type="button"
                  className={`rowline ${toateTemele ? "on" : ""}`}
                  title={tr("struct.vp.toate_temele_titlu")}
                  onClick={() => {
                    setTema(TOATE);
                    setTipDeschis(null);
                    setRandDeschis(null);
                  }}
                >
                  <span className="sp" style={{ textAlign: "left" }}>
                    {tr("struct.vp.toate_temele")}
                  </span>
                  <span className="note mono ver-cat-n">
                    {nr(contorFaraTema.total)}
                  </span>
                  {contorFaraTema.neconform > 0 ? (
                    <span className="chip c-bad">{nr(contorFaraTema.neconform)}</span>
                  ) : contorFaraTema.la_limita > 0 ? (
                    <span className="chip c-amb">{nr(contorFaraTema.la_limita)}</span>
                  ) : (
                    <span className="chip c-ok">✓</span>
                  )}
                </button>
                {toateTematicile.map((univers) => {
                  const t = tematici.find((x) => x.cheie === univers.cheie);
                  return (
                    <button
                      key={univers.cheie}
                      type="button"
                      className={`rowline ${
                        activa?.cheie === univers.cheie ? "on" : ""
                      } ${t ? "" : "ver-nul"}`}
                      title={tr(univers.ce as Cheie)}
                      onClick={() => {
                        setTema(univers.cheie);
                        setTipDeschis(null);
                        setRandDeschis(null);
                      }}
                    >
                      <span className="sp" style={{ textAlign: "left" }}>
                        {tr(univers.eticheta as Cheie)}
                      </span>
                      <span className="note mono ver-cat-n">
                        {nr(t?.total ?? 0)}
                      </span>
                      {(t?.neconform ?? 0) > 0 ? (
                        <span className="chip c-bad">{nr(t!.neconform)}</span>
                      ) : (t?.la_limita ?? 0) > 0 ? (
                        <span className="chip c-amb">{nr(t!.la_limita)}</span>
                      ) : (t?.conform ?? 0) > 0 ? (
                        <span className="chip c-ok">✓</span>
                      ) : (
                        <span className="chip c-mut">—</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* --- Dreapta: ce se arată, apoi tipurile ------------------------ */}
        <div className="ver-dreapta">
          {/* PROPOZIȚIA FILTRULUI, deasupra listei pe care o descrie. Cifrele
              de pe pastile spun cât are fiecare valoare; asta spune ce se
              vede ACUM și din ce se compune — inclusiv cuvântul «și», ca
              intersecția să nu mai fie de ghicit. */}
          <div className="ver-activ">
            <b>
              {tr("struct.vp.din", {
                n: nr(nrFiltrate), total: nr(contor.total),
              })}
            </b>
            <span className="note">
              {trn("struct.vp.verificari#n", contor.total)}
            </span>
            {facete.length ? (
              <>
                <span className="ver-slash">·</span>
                {facete.map((f, i) => (
                  <span key={f.grup} className="ver-fchip">
                    {i > 0 && <span className="ver-si">{tr("struct.vp.si")}</span>}
                    <span className="ver-fchip-corp">
                      <span className="ver-fchip-grup">{f.grup}</span>
                      {f.text}
                      <button
                        type="button"
                        className="ver-fchip-x"
                        title={tr("struct.vp.scoate", {
                          grup: f.grup.toLowerCase(),
                        })}
                        onClick={f.sterge}
                      >
                        ×
                      </button>
                    </span>
                  </span>
                ))}
              </>
            ) : (
              <span className="note">{tr("struct.vp.niciun_filtru")}</span>
            )}
            <div className="sp" />
            {facete.length > 1 && (
              <button className="btn btng ver-desfa" onClick={desfaTot}>
                {tr("struct.vp.desfa")}
              </button>
            )}
          </div>

          <div className="ver-reguli">
            {grupe.map(({ t, tipuri }) => (
              <div key={t.cheie || "_"} className="ver-tema-grup">
                <div className="ver-tema-cap">
                  <b>{tr(t.eticheta as Cheie)}</b>
                  <span className="note">
                    {trn("struct.vp.tema_sumar#n", t.total,
                         { tipuri: tipuri.length })}
                    {t.neconform
                      ? tr("struct.vp.tema_pica", { n: nr(t.neconform) }) : ""}
                  </span>
                </div>
                <p className="note ver-tema-ce">{tr(t.ce as Cheie)}</p>
                {tipuri.map((g) => (
                  <CardTip
                    key={`${t.cheie}|${g.tip}`}
                    g={g}
                    id={id}
                    cloneId={cloneId}
                    tematica={t.cheie}
                    verdicte={verdicte}
                    versiune={versiune}
                    solutie={solutie}
                    catalog={catalog}
                    deschis={tipDeschis === `${t.cheie}|${g.tip}`}
                    randDeschis={randDeschis}
                    onTip={() => {
                      const k = `${t.cheie}|${g.tip}`;
                      setTipDeschis(tipDeschis === k ? null : k);
                      setRandDeschis(null);
                    }}
                    onRand={(k) => setRandDeschis(randDeschis === k ? null : k)}
                  />
                ))}
              </div>
            ))}

            {!nrTipuri && (
              <p className="note ver-nimic">
                {tr("struct.vp.nimic")}{" "}
                <button className="btn btng" onClick={desfaTot}>
                  {tr("struct.vp.desfa")}
                </button>
              </p>
            )}

            {/* trasabilitatea — „0 înseamnă zero"; stă la capătul listei, nu
                sub cele două coloane: panourile își poartă singure scrollul */}
            <Trasabilitate id={id} solutie={solutie} acoperire={sumarAici.acoperire} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** TREAPTA DE SEVERITATE, scrisă în alt alfabet decât verdictul.
 *
 *  Cele două scări ale ecranului răspund la întrebări diferite: verdictul
 *  spune ce a ieșit din calcul (pică / la limită / trece), severitatea spune
 *  cât de departe se propagă o picată din categoria asta, adică ordinea în
 *  care se repară. Purtate amândouă în pastile rotunde roșu/chihlimbar/gri,
 *  se citeau ca aceeași scară — „HIGH critică" roșu, lipit de „12 trec"
 *  verde, arăta ca douăsprezece picate.
 *
 *  Aici severitatea nu ia NICIO culoare de verdict:
 *
 *    * altă formă — badge cu colțuri drepte, nu pastilă rotundă;
 *    * altă cerneală — neutră, pe fundalul aplicației, cu conturul mai închis
 *      la treapta critică. Diferența dintre trepte e greutatea, nu tonul;
 *    * un contor de patru bare, care spune treapta și cu culorile stinse — 4
 *      pline la critică, 3 la globală, 2 la importantă, 1 la locală.
 *
 *  Cifra rămâne lângă badge (câte verificări are treapta), ca la pastile. */
function Treapta({ s }: { s: (typeof SEVERITATI)[number] }) {
  const tr = useT();
  const bare = Array.from({ length: NR_TREPTE }, (_, i) => i + 1);
  return (
    <>
      <span className="sev-rang" aria-hidden="true">
        {bare.map((i) => (
          <i key={i} className={i <= NR_TREPTE + 1 - s.rang ? "pe" : ""} />
        ))}
      </span>
      {/* numai eticheta, în limba interfeței: «CRITICAL critică» spunea
          același lucru de două ori, în două limbi */}
      <span>{tr(s.eticheta)}</span>
    </>
  );
}

/** O fațetă de filtrare: capul cu numele și rostul ei, pastilele dedesubt.
 *  Cele trei arată la fel anume — sunt același gest, pe trei întrebări. */
function Fateta({
  titlu,
  ce,
  gol,
  onGol,
  children,
}: {
  titlu: string;
  ce: string;
  gol: boolean;
  onGol: () => void;
  children: ReactNode;
}) {
  const tr = useT();
  return (
    <div className="ver-fateta">
      <div className="ver-fateta-cap">
        <span className="ob-lbl">{titlu}</span>
        <span className="note">{ce}</span>
        <div className="sp" />
        {!gol && (
          <button type="button" className="btn btng ver-fateta-x" onClick={onGol}>
            {tr("struct.vp.toate")}
          </button>
        )}
      </div>
      <div className="ver-pastile">{children}</div>
    </div>
  );
}

/** Prag de VERDE: peste atâta procent, cu picatele rămase toate locale,
 *  structura e în regulă — restul sunt reparații de element, nu semne că
 * modelul e greșit („dacă e peste 95 % și fără picate
 *  high să apară cu verde, nu cu maro"). */
const PRAG_VERDE_PCT = 95;

/** Cât din ce s-a PUTUT măsura trece.
 *
 *  Numitorul e trece + la limită + pică, nu totalul: o verificare
 *  `neaplicabil` nu se cere (scutire din normativ, cale de presiune
 *  neguvernantă), iar una `informativ` nu e un verdict. Numărate ca reușite,
 *  ar da un procent care crește cu fiecare verificare pe care lanțul nu o
 *  cere — de asta procentul își spune și numitorul, dedesubt.
 *
 *  Cadranul NU se filtrează: e starea soluției, nu a listei de sub el. O
 *  rotiță care sare la 100 % fiindcă tocmai ai apăsat «trec» ar fi un
 *  indicator care se lasă păcălit de propriul filtru.
 *
 *  CULOAREA nu e „orice picată = chihlimbar": o picată GLOBALĂ (moduri,
 *  drift, validitatea analizei) schimbă eforturile din tot ce urmează, deci
 *  restul verdictelor sunt provizorii — aia merită culoare. Câteva picate
 *  locale peste 95 % nu spun asta. */
function Cadran({
  contor,
  globale,
}: {
  contor: ReturnType<typeof numaraVerdicte>;
  /** câte dintre picate sunt de prioritate HIGH */
  globale: number;
}) {
  const tr = useT();
  const trn = useTn();
  const trec = contor.conform + contor.la_limita;
  const procent = contor.masurate
    ? Math.round((trec / contor.masurate) * 1000) / 10
    : 0;
  const verde = !globale && procent >= PRAG_VERDE_PCT;
  const R = 30;
  const circumferinta = 2 * Math.PI * R;
  const plin = (procent / 100) * circumferinta;
  return (
    <div className="ver-cadran">
      <svg width="76" height="76" viewBox="0 0 76 76">
        <circle cx="38" cy="38" r={R} fill="none" stroke="#e7e7e7" strokeWidth="9" />
        <circle
          cx="38"
          cy="38"
          r={R}
          fill="none"
          stroke={verde ? "var(--ok)" : contor.neconform ? "var(--amb)" : "var(--ok)"}
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={`${plin} ${circumferinta - plin}`}
          transform="rotate(-90 38 38)"
        />
        <text x="38" y="43" textAnchor="middle" fontSize="15" fontWeight="600" fill="currentColor">
          {nr(procent, procent < 100 && procent > 99 ? 1 : 0)} %
        </text>
      </svg>
      <span className="note">
        {tr("struct.vp.cadran", {
          trec: nr(trec), masurate: nr(contor.masurate),
        })}
        {globale > 0 && (
          <>
            {" · "}
            <span style={{ color: "var(--bad)" }}>
              {trn("struct.vp.globale#n", globale)}
            </span>
          </>
        )}
      </span>
    </div>
  );
}

/** Direcțiile pe care s-a măsurat un tip, cu verdictul fiecăreia.
 *
 *  „Se calculează pe ambele direcții?" era întrebarea care nu avea răspuns pe
 *  ecran: driftul se măsoară pe X și pe Y, dar tabloul arăta un singur rând, cu
 * direcția guvernantă în numele elementului. Acum fiecare
 *  direcție e un rând, iar capul tipului le arată pe amândouă dintr-o privire —
 *  inclusiv atunci când una trece și cealaltă nu. */
function directiile(celule: CelulaVerificari[]) {
  const m = new Map<string, CelulaVerificari[]>();
  for (const x of celule) {
    if (!x.directie) continue;
    m.set(x.directie, [...(m.get(x.directie) ?? []), x]);
  }
  return [...m.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([dir, lista]) => {
      // verdictul direcției e cel mai grav dintre rândurile ei, iar cifra e a
      // celui mai strâns dintre ele (`E`/`R` ale celulei)
      const verdict =
        ORDINEA_VERDICTELOR.find((v) => lista.some((x) => x.verdict === v)) ?? "conform";
      const cap = lista.find((x) => x.verdict === verdict) ?? lista[0];
      return {
        dir,
        verdict,
        tip_text: [traduceRezultat(cap.E),
                   cap.R && t("struct.vp.fata_de", { prag: traduceRezultat(String(cap.R)) })]
          .filter(Boolean)
          .join(" "),
      };
    });
}

function Directie({
  dir,
  verdict,
  detaliu,
}: {
  dir: string;
  verdict: Verdict;
  detaliu?: string;
}) {
  const tr = useT();
  return (
    <span
      className={`ver-dir c-${TON[verdict]}`}
      data-tip={tr("struct.vp.dir_tip", {
        dir,
        detaliu: detaliu ? tr("struct.vp.dir_detaliu", { ce: traduceRezultat(detaliu) }) : "",
        verdict: tr(CHIP[verdict]),
      })}
    >
      {dir}
    </span>
  );
}

/** Un TIP de verificare: capul cu contorul, formula și articolul din catalog,
 *  apoi rândurile lui (elementele), fiecare expandabil cu tot detaliul.
 *
 *  Capul se desenează din celule; rândurile se cer abia la deschidere, câte
 *  o pagină, cu filtrul de verdict al ecranului — pe P+4 un singur tip de
 *  grindă are mii de rânduri. Serverul le dă deja ordonate: cele mai
 *  strânse întâi (R/E crescător), fără R/E la sfârșit. */
function CardTip({
  g,
  id,
  cloneId,
  tematica,
  verdicte,
  versiune,
  solutie,
  catalog,
  deschis,
  randDeschis,
  onTip,
  onRand,
}: {
  g: GrupTip;
  id: string;
  cloneId: string | null;
  tematica: string;
  verdicte: readonly string[];
  versiune: string;
  solutie?: string | null;
  catalog: CatalogVerificari | null;
  deschis: boolean;
  randDeschis: string | null;
  onTip: () => void;
  onRand: (k: string) => void;
}) {
  const tr = useT();
  const trn = useTn();
  const [limita, setLimita] = useState(PAGINA_CARD);
  useEffect(() => setLimita(PAGINA_CARD), [deschis]);
  const cat = catalog ? categoriaDin(catalog, g.tip) : null;
  const unic = g.total === 1 ? g.celule[0] : null;
  const dirs = useMemo(() => directiile(g.celule), [g.celule]);
  const areDirectii = dirs.length > 0;

  // RÂNDURILE, ținute cu cererea lor: «încă 200» lasă pe ecran pagina de
  // dinainte până sosește cea mare, iar un filtru nou arată locul rândurilor
  const cerere = `${cloneId}|${g.tip}|${tematica}|${verdicte.join(",")}|${versiune}`;
  const [pagina, setPagina] = useState<{
    cerere: string; randuri: RandVerificare[]; total: number; eroare?: boolean;
  } | null>(null);
  useEffect(() => {
    if (!deschis || !cloneId) return;
    let viu = true;
    getVerificariRanduri(id, cloneId, {
      grup: g.tip, tematica: tematica || null, verdicte, inceput: 0, cate: limita,
    })
      .then((p) => { if (viu) setPagina({ cerere, randuri: p.randuri, total: p.total }); })
      .catch(() => { if (viu) setPagina({ cerere, randuri: [], total: 0, eroare: true }); });
    return () => { viu = false; };
  }, [deschis, id, cloneId, g.tip, tematica, verdicte, cerere, limita]);
  const aici = pagina?.cerere === cerere ? pagina : null;
  const randuri = aici?.randuri ?? [];
  const totalRanduri = aici?.total ?? 0;

  return (
    <div className={`card ver-regula ${deschis ? "on" : ""}`}>
      <div
        className="ver-cap"
        role="button"
        tabIndex={0}
        onClick={onTip}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onTip();
          }
        }}
      >
        <span className="ver-sageata">{deschis ? "▾" : "▸"}</span>
        <span className="ver-titlu">
          {/* explicația COMPLETĂ atârnă de ⓘ, nu de titlu: titlul e butonul
              care deschide cardul, iar două acțiuni pe același cuvânt se bat
              cap în cap („da, mai bine pe un i"). */}
          <b>
            <TitluVerificare
              catalog={catalog}
              tip={g.tip}
              text={g.eticheta}
              icoana
              aratNumele={false}
              aratPasul={false}
            />
          </b>
          <span className="note">
            {nr(g.total)} {trn("struct.vp.verificari#n", g.total)}
            {areDirectii
              ? trn("struct.vp.pe_directii#n", dirs.length, {
                  lista: dirs.map((d) => d.dir).join(` ${tr("struct.vp.si")} `),
                })
              : ""}
            {unic?.E
              ? unic.R
                ? tr("struct.vp.masurat_card_prag", {
                    E: traduceRezultat(String(unic.E)),
                    R: traduceRezultat(String(unic.R)),
                  })
                : tr("struct.vp.masurat_card", { E: traduceRezultat(String(unic.E)) })
              : ""}
          </span>
        </span>
        {/* verdictul FIECĂREI direcții, în capul cardului: pe drift, „trece"
            fără să spui pe care direcție nu înseamnă nimic */}
        {areDirectii && (
          <span className="ver-dirs">
            {dirs.map((d) => (
              <Directie
                key={d.dir}
                dir={d.dir}
                verdict={d.verdict}
                detaliu={d.tip_text}
              />
            ))}
          </span>
        )}
        {/* treapta de severitate a CATEGORIEI: ea spune ce se repară întâi,
            iar când e neblocantă, că picata se raportează fără să oprească
            livrarea */}
        {(() => {
          const s = SEVERITATI.find((x) => x.cod === severitateaDin(catalog, g.tip));
          if (!s) return null;
          return (
            <span className={`sev s-${s.cod}`}
                  title={tr("struct.vp.sev_titlu", { cod: s.cod, ce: tr(s.ce) })}>
              <Treapta s={s} />
            </span>
          );
        })()}
        {cat?.blocheaza === false && (
          <span
            className="chip c-mut"
            title={tr("struct.vp.raportata_titlu")}
          >
            {tr("struct.vp.raportata")}
          </span>
        )}
        {VERDICTE.map((v) =>
          g[v.key] ? (
            <span key={v.key} className={`chip c-${v.ton}`}>
              {nr(g[v.key])} {tr(v.pastila)}
            </span>
          ) : null
        )}
        {g.informativ ? (
          <span className="chip c-out">
            {tr("struct.vp.informative", { n: nr(g.informativ) })}
          </span>
        ) : null}
      </div>

      {deschis && (
        <div className="ver-detaliu">
          {/* O PROPOZIȚIE, o dată pentru toate rândurile categoriei. Teoria
              (formulă, simboluri, divergență, soluții) e la hover pe titlu
              sau sub „explicația completă". Soluțiile se listează în pagină
              doar când chiar are ce rezolva cineva. */}
          {cat ? (
            <div className="ver-daca">
              <RezumatCategorie tip={g.tip} cat={cat} />
              {g.neconform + g.la_limita > 0 && cat.solutii?.length > 0 && (
                <div className="ver-solutii">
                  <span className="ob-lbl">{tr("struct.vp.ce_se_face")}</span>
                  <ol>
                    {cat.solutii.map((sol, i) => (
                      <li key={i}>
                        {sol.ce}
                        <span className="note"> · {costScurt(sol.cost)}</span>
                        {sol.automat ? (
                          <span className="note">{tr("struct.vp.bucla")}</span>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          ) : (
            <p className="note">
              {tr("struct.vp.fara_categorie")}
            </p>
          )}

          {!aici ? (
            <SeIncarca randuri={3} />
          ) : aici.eroare ? (
            <p className="note bad">{tr("struct.vp.esec_randuri")}</p>
          ) : (
          <table className="ver-tabel">
            <thead>
              <tr>
                <th style={{ width: 22 }} />
                <th>{tr("struct.vp.col_verificarea")}</th>
                {areDirectii && (
                  <th style={{ width: 64 }}>{tr("struct.vp.col_directie")}</th>
                )}
                <th style={{ width: 86 }}>{tr("struct.top.nivel")}</th>
                <th style={{ width: 190 }}>{tr("struct.vp.col_masurat")}</th>
                <th style={{ width: 116 }}>{tr("struct.tipver.col_re")}</th>
                <th style={{ width: 86 }}>{tr("struct.vp.grup_verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {randuri.map((x, i) => {
                const k = `${g.tip}|${x.element ?? ""}|${x.nume}|${i}`;
                const eDeschis = randDeschis === k;
                const ePlan = /^(GR|ST|PB|OP)\d/.test(String(x.element ?? ""));
                // Pe un rând de direcție, „elementul" E direcția — și ea
                // are deja coloana ei. Locul se dă atunci punctului care
                // guvernează (colțul, cazul de încărcare); numele verificării
                // ar repeta al treilea rând titlul cardului.
                const eticheta = x.directie
                  ? x.v.punct ?? x.nume
                  : x.element ?? "—";
                return (
                  <Fragment key={k}>
                    <tr
                      className={eDeschis ? "on" : undefined}
                      onClick={() => onRand(k)}
                    >
                      <td className="ver-sageata">{eDeschis ? "▾" : "▸"}</td>
                      <td>
                        {x.cod && <span className="ref">{x.cod}</span>}{" "}
                        {ePlan ? (
                          <a
                            href={hashElement(id, String(x.element), solutie)}
                            onClick={(e) => e.stopPropagation()}
                            title={x.tipElement ? t(TIP_ETICHETA[x.tipElement]) : undefined}
                          >
                            {x.element}
                          </a>
                        ) : (
                          <span>{eticheta}</span>
                        )}
                        {!x.directie && x.v.punct ? (
                          <span className="note"> · {x.v.punct}</span>
                        ) : null}
                        {!x.directie && g.total > 1 && x.nume !== g.eticheta ? (
                          <div className="note">{x.nume}</div>
                        ) : null}
                      </td>
                      {areDirectii && (
                        <td>
                          {x.directie ? (
                            <Directie dir={x.directie} verdict={x.verdict} />
                          ) : (
                            <span className="note">—</span>
                          )}
                        </td>
                      )}
                      <td className="note">{x.nivel ?? "—"}</td>
                      <td className="mono">
                        {x.v.E != null ? String(x.v.E) : "—"}
                        {x.v.R != null ? (
                          <span className="note"> {String(x.v.R)}</span>
                        ) : null}
                      </td>
                      <td>
                        <BaraRE re={x.RE} ton={TON[x.verdict] as "bad"} />
                      </td>
                      <td>
                        <span className={`chip c-${TON[x.verdict]}`}>
                          {tr(CHIP[x.verdict])}
                        </span>
                        {x.verdict === "neconform" &&
                          categoriaDin(catalog, x.tip ?? "")?.blocheaza ===
                            false && (
                            <span
                              className="chip c-mut"
                              title={tr("struct.vp.raportata_titlu")}
                            >
                              {" "}
                              {tr("struct.vp.raportata")}
                            </span>
                          )}
                      </td>
                    </tr>
                    {eDeschis && (
                      <tr className="ver-rand-detaliu">
                        <td />
                        <td colSpan={areDirectii ? 6 : 5}>
                          <VerificareDetaliu
                            v={x.v}
                            pas={x.pas}
                            catalog={catalog}
                            linkPas={linkAncora(id, solutie, x.ancora)?.href}
                            explicatie={false}
                          />
                          {x.tip && x.verdict === "neconform" && (
                            <a href={hashVerificare(id, x.tip, solutie)} className="note">
                              {tr("struct.vp.toate_care_pica")}
                            </a>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {totalRanduri > randuri.length && (
                <tr>
                  <td colSpan={areDirectii ? 7 : 6} className="note">
                    {tr("struct.vp.se_arata", {
                      n: nr(randuri.length), total: nr(totalRanduri),
                    })}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setLimita(randuri.length + PAGINA_INCA);
                      }}
                    >
                      {tr("struct.vp.incarca")}
                    </a>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          )}
        </div>
      )}
    </div>
  );
}

function Trasabilitate({
  id,
  solutie,
  acoperire,
}: {
  id: string;
  solutie?: string | null;
  acoperire: AcoperireVerificari[];
}) {
  // pliată implicit: e proba că nu lipsește nimic din numărătoare, nu ceva ce
  // se citește la fiecare deschidere a ecranului
  const tr = useT();
  const [deschis, setDeschis] = useState(false);
  if (!acoperire.length) return null;
  return (
    <>
      <button
        type="button"
        className="ver-trasa-cap"
        onClick={() => setDeschis(!deschis)}
      >
        <span className="ver-sageata">{deschis ? "▾" : "▸"}</span>
        {tr("struct.vp.trasa_titlu")}
      </button>
      {!deschis ? null : (
      <>
      <p className="note">
        {tr("struct.vp.trasa_ce")}
      </p>
      <div className="card" style={{ overflow: "hidden", marginTop: 8 }}>
        <table className="ver-tabel">
          <thead>
            <tr>
              <th>{tr("struct.vp.col_calculul")}</th>
              <th style={{ width: 110, textAlign: "right" }}>
                {tr("struct.vp.col_verificari")}
              </th>
              <th style={{ width: 90, textAlign: "right" }}>
                {tr("struct.vp.col_picate")}
              </th>
              <th style={{ width: 90 }}>{tr("struct.vp.col_citit")}</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {acoperire.map((a) => (
              <tr key={a.pas}>
                <td>{a.subiect || a.nume || titluPas(a.pas)}</td>
                <td className="mono" style={{ textAlign: "right" }}>
                  {nr(a.nr_verificari ?? 0)}
                </td>
                <td className="mono" style={{ textAlign: "right" }}>
                  {a.nr_picate ? (
                    <span className="chip c-bad">{nr(a.nr_picate)}</span>
                  ) : (
                    "0"
                  )}
                </td>
                <td>
                  {a.citit ? (
                    <span className="chip c-ok">✓</span>
                  ) : (
                    <span className="chip c-amb">{tr("struct.vp.necitit")}</span>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  <a className="note" href={hashSectiune(id, a.pas, solutie ?? null)}>
                    {tr("struct.vp.vezi_calculul")}
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}
    </>
  );
}
